import React, { useMemo, useState } from "react";
import type { GeoJsonFeature } from "../../services/ColombiaMapService";
import colombiaMapService from "../../services/ColombiaMapService";
import { POPULATION_DATA } from "../../shared/population-colors";

type Props = {
  feature: GeoJsonFeature;
  onClose: () => void;
};

const PopupDetail: React.FC<Props> = ({ feature, onClose }) => {
  // Propiedades seguras (puede venir null)
  const p = (feature.properties ?? {}) as Record<string, unknown>;

  const nombre = String(p["Nombre"] ?? "-");
  const direccion = String(p["Domicilios completos"] ?? "");
  const muniTxt = String(p["MUNICIPIO.1"] ?? "");
  const [municipio, departamento] = muniTxt
    .split(",")
    .map((s) => (s ?? "").trim());

  const notaUbicacion = String(p["NOTA UBICACIÓN"] ?? "");
  const showNota =
    notaUbicacion.includes("No se presenta la ubicación precisa");

  const poblacionAtendida = String(
    (p["Población atendida"] ??
      p["Poblaci\u00f3n atendida"] ??
      "") as string
  );

  // Helpers
  const capitalizarPrimeraLetra = (cadena: string) =>
    (cadena || "")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  const buildPopulation = useMemo(() => {
    const name = (poblacionAtendida || "").toLowerCase();
    const arr: { name: string; color: string; icon: string; label: string }[] =
      [];
    if (name.includes("mayores")) arr.push(POPULATION_DATA["Personas Mayores"]);
    if (name.includes("niños"))
      arr.push(POPULATION_DATA["Niños, Niñas y/o Adolescentes"]);
    if (name.includes("discapacidad"))
      arr.push(POPULATION_DATA["Personas con Discapacidad"]);
    if (name.includes("cuidador")) arr.push(POPULATION_DATA["Cuidadores"]);
    return arr;
  }, [poblacionAtendida]);

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const toggleTooltip = () => setTooltipVisible((v) => !v);

  const selectItem = () => {
    colombiaMapService.setSelectItem(feature);
    onClose();
  };

  return (
    <div className="bg-white/85 rounded-xl shadow-lg min-w-72 w-full md:w-[350px] text-gray-800 relative mx-auto my-auto overflow-y-auto">
      {/* Cerrar */}
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-gray-500 hover:bg-gray-700 text-white rounded-full z-10"
      >
        <i className="material-icons-outlined text-white text-base">close</i>
      </button>

      {/* Header */}
      <div className="text-base font-semibold py-2 pl-4 pr-10 rounded-t-xl shadow bg-white/0 leading-6">
        <h3>{nombre}</h3>
      </div>

      {/* Contenido */}
      <div className="px-2 pb-2">
        {/* Dirección + Nota ubicación */}
        <div className="flex justify-between">
          {direccion && direccion.trim() !== "" && (
            <p className="text-[13px] md:text-base mt-4 flex items-start">
              <i className="material-icons-outlined text-primary text-base md:text-xl mr-1">
                location_on
              </i>
              <span className="mt-1">
                {capitalizarPrimeraLetra(direccion)}
              </span>
            </p>
          )}

          {showNota && (
            <div
              className="relative w-[29px] mt-3 h-7 ml-4 cursor-pointer"
              onClick={toggleTooltip}
            >
              <span className="flex items-center justify-center bg-white rounded">
                <i className="material-icons-outlined text-primary">info</i>
              </span>
              {tooltipVisible && (
                <div className="absolute w-44 right-full bottom-[1.5rem] mb-2 bg-white text-primary text-xs rounded px-2 py-1 shadow-md">
                  {notaUbicacion}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Departamento / Municipio */}
        <div className="flex justify-between mt-3">
          <div>
            <p className="ml-3 text-xs md:text-sm">
              <strong>Departamento:</strong>{" "}
              {capitalizarPrimeraLetra(departamento)}
            </p>
            <p className="ml-3 text-xs md:text-sm">
              <strong>Municipio:</strong>{" "}
              {capitalizarPrimeraLetra(municipio)}
            </p>
          </div>
        </div>

        {/* Poblaciones (chips con icono) */}
        <div className="flex justify-between items-center mt-2">
          <div
            className={`grid gap-1 p-1 ${
              buildPopulation.length > 1
                ? "grid-cols-2"
                : "grid-cols-1"
            }`}
          >
            {buildPopulation.map((pop, idx) => (
              <div key={idx} className="flex items-center">
                <div
                  className="rounded-full w-8 h-8 mr-1 flex items-center justify-center border flex-shrink-0 aspect-square"
                  style={{ backgroundColor: pop.color }}
                >
                  <img
                    src={`/assets/images/${pop.icon}`}
                    alt={`${pop.label} Icon`}
                    className="w-5 h-5 svg-icon-white"
                  />
                </div>
                <p className="text-xs md:text-sm">{pop.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Botón Ver detalles */}
        <div className="flex mt-4">
          <button
            onClick={selectItem}
            className="ml-auto py-2 px-4 bg-primary text-white font-medium text-base rounded-full transition duration-300"
          >
            Ver detalles
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupDetail;
